import React, { useState } from 'react';
import { UploadFormData } from '../types';
import { ImageUploadService, UploadProgress } from '../services/imageUpload';
import { PlateService } from '../services/plateService';

interface UploadFormProps {
  onSuccess?: () => void;
}

const UploadForm: React.FC<UploadFormProps> = ({ onSuccess }) => {
  const [formData, setFormData] = useState<UploadFormData>({
    images: [],
    state: ''
  });
  const [dragActive, setDragActive] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState<UploadProgress | null>(null);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [uploadSuccess, setUploadSuccess] = useState(false);
  const [uploadedCount, setUploadedCount] = useState(0);

  const handleDrag = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  };

  const addFiles = (files: FileList | File[]) => {
    const fileArray = Array.from(files);
    const imageFiles = fileArray.filter(file => file.type.startsWith('image/'));
    if (imageFiles.length > 0) {
      setFormData(prev => ({
        ...prev,
        images: [...prev.images, ...imageFiles]
      }));
    }
  };

  const removeFile = (index: number) => {
    setFormData(prev => ({
      ...prev,
      images: prev.images.filter((_, i) => i !== index)
    }));
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    
    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      addFiles(e.dataTransfer.files);
    }
  };

  const handleFileInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      addFiles(e.target.files);
      e.target.value = '';
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (formData.images.length === 0) return;

    setUploading(true);
    setUploadError(null);
    setUploadSuccess(false);
    setUploadedCount(0);
    setUploadProgress({ progress: 0, status: 'uploading', currentFile: 1, totalFiles: formData.images.length });

    try {
      const imageUrls = await ImageUploadService.uploadMultipleImages(
        formData.images,
        (progress) => setUploadProgress(progress)
      );

      for (let i = 0; i < imageUrls.length; i++) {
        await PlateService.savePlate({
          imageUrl: imageUrls[i],
          state: formData.state || undefined
        });
        setUploadedCount(i + 1);
      }

      setUploadSuccess(true);
      setFormData({ images: [], state: '' });
      
      setTimeout(() => {
        setUploadSuccess(false);
        setUploadProgress(null);
        onSuccess?.();
      }, 3000);

    } catch (error) {
      console.error('Upload error:', error);
      setUploadError(error instanceof Error ? error.message : 'Upload failed');
      setUploadProgress(null);
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="bg-gray-50 dark:bg-slate-700/30 rounded-xl p-3">
      <h2 className="text-base font-semibold text-gray-900 dark:text-white mb-3">Upload License Plates</h2>
      
      <form onSubmit={handleSubmit} className="space-y-3">
        <div
          className={`relative border-2 border-dashed rounded-xl p-4 text-center transition-all duration-200 ${
            dragActive 
              ? 'border-primary-500 bg-primary-50 dark:bg-primary-500/10' 
              : 'border-gray-300 dark:border-slate-600 hover:border-gray-400 dark:hover:border-slate-500'
          }`}
          onDragEnter={handleDrag}
          onDragLeave={handleDrag}
          onDragOver={handleDrag}
          onDrop={handleDrop}
        >
          <input
            type="file"
            accept="image/*"
            multiple
            onChange={handleFileInput}
            className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
          />
          
          {formData.images.length > 0 ? (
            <div className="space-y-2">
              <div className="w-16 h-16 mx-auto bg-green-100 dark:bg-green-500/20 rounded-xl flex items-center justify-center">
                <svg className="w-8 h-8 text-green-600 dark:text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              </div>
              <p className="text-sm text-gray-600 dark:text-slate-300 font-medium">
                {formData.images.length} image{formData.images.length !== 1 ? 's' : ''} selected
              </p>
              <p className="text-xs text-gray-500 dark:text-slate-400">Click or drag to add more</p>
            </div>
          ) : (
            <div className="space-y-2">
              <div className="w-16 h-16 mx-auto bg-gray-100 dark:bg-slate-600/50 rounded-xl flex items-center justify-center">
                <svg className="w-8 h-8 text-gray-400 dark:text-slate-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                </svg>
              </div>
              <p className="text-sm text-gray-600 dark:text-slate-400">
                Drag and drop images here, or click to select multiple
              </p>
            </div>
          )}
        </div>

        {formData.images.length > 0 && (
          <div className="space-y-2">
            <p className="text-sm font-medium text-gray-700 dark:text-slate-300">Selected files:</p>
            <ul className="max-h-32 overflow-y-auto space-y-1">
              {formData.images.map((file, index) => (
                <li 
                  key={`${file.name}-${index}`}
                  className="flex items-center justify-between text-sm text-gray-600 dark:text-slate-400 bg-white dark:bg-slate-700/50 px-3 py-2 rounded-lg border border-gray-200 dark:border-slate-600"
                >
                  <span className="truncate flex-1">{file.name}</span>
                  <button
                    type="button"
                    onClick={() => removeFile(index)}
                    disabled={uploading}
                    className="ml-2 text-red-500 hover:text-red-600 dark:text-red-400 dark:hover:text-red-300 disabled:opacity-50 disabled:cursor-not-allowed"
                    aria-label={`Remove ${file.name}`}
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </li>
              ))}
            </ul>
          </div>
        )}

        {uploadProgress && (
          <div className="space-y-2">
            <div className="flex justify-between text-sm text-gray-600 dark:text-slate-400">
              <span>
                {uploadProgress.currentFile && uploadProgress.totalFiles
                  ? `Uploading image ${uploadProgress.currentFile} of ${uploadProgress.totalFiles}...`
                  : 'Uploading...'}
              </span>
              <span>{Math.round(uploadProgress.progress)}%</span>
            </div>
            <div className="w-full bg-gray-200 dark:bg-slate-600 rounded-full h-2">
              <div 
                className="bg-primary-500 h-2 rounded-full transition-all duration-300"
                style={{ width: `${uploadProgress.progress}%` }}
              />
            </div>
          </div>
        )}

        {uploadSuccess && (
          <div className="bg-green-100 dark:bg-green-500/20 border border-green-400 dark:border-green-500/50 text-green-700 dark:text-green-300 px-4 py-3 rounded-xl">
            <div className="flex items-center">
              <svg className="w-5 h-5 mr-2 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
              <span>
                {uploadedCount === 1 
                  ? 'Upload successful! Your license plate has been added.'
                  : `${uploadedCount} license plates uploaded successfully!`}
              </span>
            </div>
          </div>
        )}

        {uploadError && (
          <div className="bg-red-100 dark:bg-red-500/20 border border-red-400 dark:border-red-500/50 text-red-700 dark:text-red-300 px-4 py-3 rounded-xl">
            <div className="flex items-center">
              <svg className="w-5 h-5 mr-2 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
              {uploadError}
            </div>
          </div>
        )}

        <div>
          <label htmlFor="state" className="block text-sm font-medium text-gray-700 dark:text-slate-300 mb-1">
            State (Optional) - applies to all plates
          </label>
          <input
            type="text"
            id="state"
            value={formData.state}
            onChange={(e) => setFormData(prev => ({ ...prev, state: e.target.value }))}
            placeholder="e.g., CA, NY, TX"
            className="w-full px-3 py-2.5 border border-gray-300 dark:border-slate-600 rounded-xl bg-white dark:bg-slate-700/50 text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-colors"
          />
        </div>

        <button
          type="submit"
          disabled={formData.images.length === 0 || uploading}
          className="w-full bg-primary-500 hover:bg-primary-600 text-white py-2.5 px-4 rounded-xl font-medium focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 dark:focus:ring-offset-slate-800 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
        >
          {uploading 
            ? `Uploading...` 
            : formData.images.length > 1 
              ? `Upload ${formData.images.length} Plates`
              : 'Upload Plate'}
        </button>
      </form>
    </div>
  );
};

export default UploadForm;
